import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useToast from '@/components/Toast/useToast';
import { subscriber } from '@/data/fetcher';
import {
  PromptCanvasNode,
  StreamEvent,
  TPromptCanvasAgentPayload,
} from '@/types/schema';
import { apiPromptsPath } from '@/utils/constants';
import { isOnChatModelStreamEvent } from '@/utils/messages';

import { useOrganizationId } from '../useSwr';
import { TArtifact, TMessage } from './types';
import {
  getStreamEventData,
  updateArtifactContent,
  updateMessageState,
  updateStreamedText,
} from './utils';

type UsePromptCanvasAgentStateInput = {
  abortController?: AbortController;
  defaultPrompt: string;
};

// todo: convert to zustand
export const usePromptCanvasAgentState = ({
  abortController,
  defaultPrompt,
}: UsePromptCanvasAgentStateInput) => {
  const { createToast } = useToast();
  const organizationId = useOrganizationId();

  const defaultArtifact: TArtifact = {
    id: uuidv4(),
    contents: [
      {
        index: 0,
        content: defaultPrompt,
      },
    ],
    current_content_index: 0,
  };

  const [messages, setMessages] = useState<TMessage[]>([]);
  const [artifact, setArtifact] = useState(defaultArtifact);
  // the index of the artifact that is currently set in the playground
  const [selectedArtifactIndex, setSelectedArtifactIndex] = useState(0);
  // the index of the artifact that is currently being viewed in the canvas modal
  const [viewingArtifactIndex, setViewingArtifactIndex] = useState(0);

  const [showDiff, setShowDiff] = useState(false);

  const isCurrentPrompt = selectedArtifactIndex === viewingArtifactIndex;

  const [isMutating, setIsMutating] = useState(false);
  const [isFirstTokenReceived, setIsFirstTokenReceived] = useState(false);

  const generate = useCallback(
    (
      params: TPromptCanvasAgentPayload,
      extra: {
        newArtifactIndex: number;
        viewingArtifactIndex: number;
        viewingArtifactContent: string;
        highlightedRange?: [number, number];
      }
    ) => {
      const { newArtifactIndex, viewingArtifactContent, highlightedRange } =
        extra;
      let isFirstFollowup = true;
      let processedChars = 0;
      setIsMutating(true);
      setIsFirstTokenReceived(false);
      subscriber<any, never, TPromptCanvasAgentPayload>(
        {
          url: `${apiPromptsPath}/canvas`,
          subscriberParser: 'passthrough',
          method: 'POST',
          json: params,
          headers: {
            'x-tenant-id': organizationId,
          },
        },
        {
          next: function (error, data) {
            if (error) {
              let errorMessage = 'Failed to stream';
              try {
                const parsedError = JSON.parse(error.message);
                errorMessage = parsedError?.detail ?? errorMessage;
              } catch (e) {
                errorMessage = error.message || errorMessage;
              }
              createToast({
                title: 'Error communicating with agent',
                description: errorMessage,
                error: true,
              });
              setIsMutating(false);
              return;
            }
            if (data) {
              if (typeof data === 'function') {
                const eventData = getStreamEventData(data);
                if (!eventData) {
                  return;
                }

                if (isOnChatModelStreamEvent(eventData)) {
                  setIsFirstTokenReceived(true);

                  if (
                    eventData.metadata?.langgraph_node &&
                    [
                      PromptCanvasNode.REWRITE_ARTIFACT_THEME,
                      PromptCanvasNode.REWRITE_ARTIFACT,
                      PromptCanvasNode.REWRITE_ARTIFACT_CUSTOM_ACTION,
                    ].includes(
                      eventData.metadata?.langgraph_node as PromptCanvasNode
                    ) &&
                    eventData.data?.chunk?.content
                  ) {
                    setViewingArtifactIndex(newArtifactIndex);
                    updateArtifactContent(
                      setArtifact,
                      (eventData.data.chunk.content as string) || '',
                      newArtifactIndex,
                      false
                    );
                  }

                  if (
                    eventData.metadata?.langgraph_node ===
                      PromptCanvasNode.UPDATE_ARTIFACT &&
                    eventData.data?.chunk?.content
                  ) {
                    if (!highlightedRange) {
                      createToast({
                        title: 'Failed to update selected text.',
                        description: 'No highlighted text found.',
                        error: true,
                      });
                      return;
                    }
                    const newHighlightedTextChunk =
                      (eventData.data.chunk.content as string) || '';

                    setViewingArtifactIndex(newArtifactIndex);
                    processedChars = updateStreamedText({
                      setArtifact,
                      newArtifactIndex,
                      viewingArtifactContent,
                      selectionStart: highlightedRange[0],
                      selectionEnd: highlightedRange[1],
                      newChunk: newHighlightedTextChunk,
                      previouslyProcessed: processedChars,
                      isStreamComplete: false,
                      createToast,
                    });
                  }

                  if (
                    eventData.metadata?.langgraph_node &&
                    [
                      PromptCanvasNode.RESPOND_TO_QUERY,
                      PromptCanvasNode.GENERATE_FOLLOWUP,
                    ].includes(
                      eventData.metadata?.langgraph_node as PromptCanvasNode
                    ) &&
                    eventData.data?.chunk?.content
                  ) {
                    updateMessageState(
                      setMessages,
                      eventData.data.chunk.content as string,
                      isFirstFollowup
                    );
                    isFirstFollowup = false;
                  }
                }

                if (
                  eventData.name === 'LangGraph' &&
                  eventData.event === StreamEvent.ON_CHAIN_END
                ) {
                  setIsMutating(false);
                }

                if (eventData.event === StreamEvent.ON_CHAIN_END) {
                  if (
                    eventData.metadata?.langgraph_node ===
                      PromptCanvasNode.UPDATE_ARTIFACT &&
                    eventData.data?.output?.artifact?.contents?.length
                  ) {
                    const finalOutput =
                      eventData.data.output.artifact.contents[
                        eventData.data.output.artifact.contents.length - 1
                      ].content;
                    if (!finalOutput) {
                      createToast({
                        title: 'Error communicating with agent',
                        description: 'Invalid data received.',
                        error: true,
                      });
                      return;
                    }
                    if (!highlightedRange) {
                      createToast({
                        title: 'Failed to update selected text.',
                        description: 'No highlighted text found.',
                        error: true,
                      });
                      return;
                    }

                    processedChars = updateStreamedText({
                      setArtifact,
                      newArtifactIndex,
                      viewingArtifactContent,
                      selectionStart: highlightedRange[0],
                      selectionEnd: highlightedRange[1],
                      newChunk: finalOutput,
                      previouslyProcessed: processedChars,
                      isStreamComplete: true,
                      createToast,
                    });
                  }
                }
              } else {
                createToast({
                  title: 'Error communicating with agent',
                  description: 'Invalid data received',
                  error: true,
                });
              }
            }
          },
        },
        abortController
      );
    },
    [abortController, createToast]
  );

  const promptToShow = artifact.contents[viewingArtifactIndex].content;

  return {
    messages,
    setMessages,
    artifact,
    setArtifact,
    selectedArtifactIndex,
    setSelectedArtifactIndex,
    viewingArtifactIndex,
    setViewingArtifactIndex,
    isCurrentPrompt,
    generate,
    isMutating,
    isFirstTokenReceived,
    showDiff,
    setShowDiff,
    promptToShow,
  };
};
